<template>
    <Form ref="elFormObj" as="">
        <v-row v-if="system === 'imperial'" density="compact">
            <v-col cols="6">
                <Field
                    name="heightFt"
                    label="Height Ft."
                    v-slot="{ field, errors }"
                    :model-value="heightFt"
                    :rules="{ required: system === 'imperial', min_value: 3, max_value: 7 }"
                >
                    <v-select
                        v-bind="field"
                        :model-value="field.value"
                        name="heightFt"
                        label=""
                        @update:model-value="onChangeFt($event)"
                        :error-messages="errors"
                        :items="BodyLengthFeetList"
                        item-title="text"
                        variant="outlined"
                        required
                    ></v-select>
                </Field>
            </v-col>
            <v-col cols="6">
                <Field
                    name="heightIn"
                    label="Height In."
                    v-slot="{ field, errors }"
                    :model-value="heightIn"
                    :rules="{ required: system === 'imperial', min_value: 0, max_value: 11.5 }"
                >
                    <v-select
                        v-bind:="field"
                        :model-value="field.value"
                        label=""
                        @update:model-value="onChangeIn"
                        :error-messages="errors"
                        item-title="text"
                        :items="BodyLengthInchesList"
                        variant="outlined"
                    ></v-select>
                </Field>
            </v-col>
        </v-row>
        <v-row v-if="system === 'metric'" density="compact">
            <v-col>
                <Field
                    name="heightMm"
                    label="Hight (mm)"
                    v-slot="{ field, errors }"
                    :model-value="heightMm"
                    :rules="{ required: system === 'metric', min_value: 1016, max_value: 2300 }"
                >
                    <v-text-field
                        v-bind="field"
                        :model-value="field.value"
                        name="heightMm"
                        label=""
                        type="number"
                        min="915"
                        max="2730"
                        :error-messages="errors"
                        @update:model-value="onChangeMm"
                        variant="outlined"
                        required
                    ></v-text-field>
                </Field>
            </v-col>
        </v-row>
    </Form>
</template>

<script lang="ts" setup>
    import type { Field, Form } from 'vee-validate';
    import { BodyLengthFeetList, BodyLengthInchesList } from '~/models';
    import { isNumber as lo_isNumber } from 'es-toolkit/compat';
    import { isString as lo_isString } from 'es-toolkit';
    import { convertMmToFtIn, convertFtInToMm } from 'x1-base/utils';
    import { initVeeRules } from '~/helpers/forms';
    
    initVeeRules([ 'required', 'min_value', 'max_value' ]);

    const props = defineProps({
        modelValue: {
            type: Number as PropType<Nilish<number>>,
            required: false
        },
        system: {
            type: String as PropType<Nilish<string>>,
            required: false,
            default: 'metric'
        }
    });

    const emit = defineEmits([
        'update:modelValue'
    ]);

    const errorMessages = ref<string[]>([]);
    const elFormObj = shallowRef<typeof Form>();
    const heightMm = ref<Nilish<number>>(null);
    const heightFt = ref<Nilish<number>>(null);
    const heightIn = ref<Nilish<number>>(null);

    const unWatchModel = watch(
        () => props.modelValue, (val: number | unknown) => {
            if (lo_isNumber(val) && val > 0) {
                heightMm.value = val;
                convertToFtIn(true);
            }
        }, { immediate: true }
    );

    function onChangeFt(val: number) {
        if (lo_isNumber(val)) {
            heightFt.value = val;
            convertToMm();
        }
    }

    function onChangeIn(val: number) {
        if (lo_isNumber(val)) {
            heightIn.value = val;
            convertToMm();
        }
    }

    function onChangeMm(val: string) {
        const newVal = parseInt(val, 10);

        if (!Number.isNaN(newVal)) {
            heightMm.value = newVal;
            convertToFtIn();
        }
    }

    async function convertToMm(noUpdate?: boolean) {
        if (lo_isNumber(heightFt.value) && lo_isNumber(heightIn.value)) {
            const valInMm = convertFtInToMm([heightFt.value, heightIn.value]);
            heightMm.value = Math.ceil(valInMm);
        } else {
            heightMm.value = null;
        }

        if (lo_isNumber(heightMm.value) && !noUpdate) {
            await nextTick();
            updateInput(heightMm.value);
        }
    }

    async function convertToFtIn(noUpdate?: boolean) {
        if (lo_isNumber(heightMm.value)) {
            const valInFtIn = convertMmToFtIn(heightMm.value);
            heightFt.value = valInFtIn[0];
            heightIn.value = valInFtIn[1];
        } else {
            heightFt.value = null;
            heightIn.value = null;
        }

        if (lo_isNumber(heightMm.value) && !noUpdate) {
            await nextTick();
            updateInput(heightMm.value);
        }
    }

    async function updateInput(val: number) {
        const validateObj = await elFormObj.value?.validate();
        let input: number | null;
                    
        if (validateObj.valid) {
            errorMessages.value = [];
            input = val;
        } else {
            errorMessages.value = Object.values(validateObj.errors);
            input = null;
        }

        emit('update:modelValue', input);
    }

    onBeforeUnmount(() => {
        unWatchModel?.();
    });
</script>

<style lang="scss" scoped>
.ethnicity-partial {
    button {
        font-size: 160%;
    }
}
</style>
